/**
 * Welcome-Kampagne — Auswertung.
 *
 * Three scopes in one view, all sharing one date range (default: Gesamt):
 *   (a) Pro Mailing  — every mailing ever selected, actual vs the picker's
 *       ETKP forecast, so the ranking that drives selection is falsifiable.
 *   (b) Gesamt       — aggregate send/engagement/order performance.
 *   (c) Nutzer       — the enrollee lifecycle funnel.
 *
 * Conversions are shown BOTH ways deliberately. `Käufer (gesamt)` counts any
 * FTY order after signup — that is what the gate acts on, since a buyer must
 * not be migrated wherever they bought. `Attribuierte Orders` counts only
 * orders carrying this campaign's banner. The first is reach, the second is
 * causation; quoting one without the other misleads (the two differed 15 vs 4
 * on 2026-07-29).
 */

import { ResponsiveBar } from '@nivo/bar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { api } from '../../helpers/apiUtils';
import Explainer, { ExplainerSection } from '../../components/Explainer';
import InfoTip from '../../components/InfoTip';

interface PerMailing {
  mailing_id: number;
  subject: string;
  campaign_name: string;
  product_code: string;
  cpa_per_order: number;
  /** Ist-CPA-Wert der Orders dieses Mailings, je Anfo bewertet (m5). */
  cpa_revenue: number;
  campaigns: number;
  recipients: number;
  positions: string;
  unique_opens: number;
  unique_clicks: number;
  orders: number;
  etkp_forecast: number | null;
  /** Auswahlwochen, deren Prognose nicht auf den heutigen CPA umgerechnet werden konnte. */
  forecast_unscaled_weeks?: number;
  weeks_selected: number;
  ai_topic: string | null;
}

interface ChannelRow {
  key: string;
  label: string;
  orders: number;
  buyers: number;
  cpa_revenue: number;
}

interface ComparisonArm {
  members: number;
  buyers: number;
  orders: number;
  cpa_revenue: number;
  webinar_registrants: number;
  webinar_registrations: number;
}

interface EvaluationData {
  /** Betriebs-Ampel. `ok` entscheidet das Backend — Schwellwerte sind Datenlogik. */
  operations: {
    sent_24h: number;
    campaigns_24h: number;
    failed_24h: number;
    /** Kontext, steuert die Farbe NICHT (die 4 Fehlschläge sind terminal, Juli). */
    failed_total: number;
    /** Nicht-terminale Zeilen älter als 2 h — hängen JETZT noch. */
    stuck_open: number;
    ok: boolean;
  };
  /** Einordnung — bewusst OHNE Zeitfilter gerechnet, trägt dafür ein Label. */
  benchmark: {
    window_start: string | null;
    welcome_messages: number;
    welcome_orders: number;
    direct_messages: number;
    direct_orders: number;
    welcome_rate_pct: number | null;
    direct_rate_pct: number | null;
    factor: number | null;
  };
  statusCounts: { status: string; count: number }[];
  channels: ChannelRow[];
  webinarEngagement: {
    /** Formularzeilen — ein erneutes Absenden ist ein weiterer Anmeldevorgang. */
    registrations: number;
    registrants: number;
    /** Personen = (Webinar, E-Mail) — Bezugsgröße für Teilnehmer / No-Shows / „noch ohne Ergebnis“. */
    persons: number;
    /** Personen, deren erste Anmeldung vor Sendestart lag — Nenner der Show-up-Quote. */
    timely: number;
    attendees: number;
    no_shows: number;
    /** Zähler/Nenner der Show-up-Quote ohne Webinare mit Tracking-Warnung (wie /webinar-auswertungen). */
    timely_ok: number;
    attendees_ok: number;
    webinars_excluded_for_tracking: number;
    /** Kohortenmitglieder mit ≥1 Webinar-Mail NACH der Anmeldung — Nenner der Anmeldequote. */
    reached: number;
    /** Grundgesamtheit, gegen die `reached` läuft. */
    cohort: number;
    /** Zähler der Quote — auf `enrolled_at` verankert wie `reached`, anders als `registrants`. */
    registrants_cohort: number;
  };
  comparison: {
    treatment: ComparisonArm;
    control: ComparisonArm;
    tenureDays: number;
    controlWindow: { start: string; end: string };
    minBuyersPerArm: number;
    decidable: boolean;
    significant: boolean;
    p1: number;
    p2: number;
    diff: number;
    pValue: number | null;
    ci95Low: number | null;
    ci95High: number | null;
  } | null;
  sendStats: {
    mailing_position: number;
    campaigns: number;
    recipients: number;
    failed: number;
    pending: number;
  }[];
  totals: {
    campaigns: number;
    recipients: number;
    unique_opens: number;
    unique_clicks: number;
    missing_metrics: number;
    attributed_orders: number;
    attributed_buyers: number;
    cpa_revenue: number;
  };
  abTest: {
    auto: { recipients: number; unique_opens: number; unique_clicks: number; orders: number };
    custom: { recipients: number; unique_opens: number; unique_clicks: number; orders: number };
    minPerArm: number;
    decidable: boolean;
    significant: boolean;
    p1: number;
    p2: number;
    diff: number;
    pValue: number | null;
    ci95Low: number | null;
    ci95High: number | null;
    nRequiredPerArm: number | null;
  } | null;
  funnel: {
    // Funnel-Stufen — alle welcome-scoped.
    enrolled: number;
    sent: number;
    opened: number;
    clicked_welcome: number;
    /** Klickte, ohne dass ein Open getrackt wurde (Pixel-Blocking/Apple MPP). */
    clicked_without_open: number;
    // Karten daneben — bewusst KEINE Stufen, die Mengen sind nicht verschachtelt.
    completed_series: number;
    /** `clicked_at`, FT-weit — überlebt die Migration, anders als `status`. */
    clicked_qualified: number;
    converted: number;
    converted_without_open: number;
    /** Auf `enrolled_at` verankert — nur diese Zahl darf gegen `converted` gerechnet werden. */
    converted_via_welcome: number;
    /** Ebenfalls `enrolled_at`-verankert — gehört neben die Trias, NICHT `totals.cpa_revenue`. */
    cpa_revenue_via_welcome: number;
  };
  perMailing: PerMailing[];
  lateBuyers: { late_buyers: number; late_orders: number };
  migratedTotal: number;
}

/**
 * Chart colours are LITERALS, not theme.palette.primary — this dashboard's
 * primary is near-black in dark mode, which renders funnel bars and scatter
 * points invisible against the background (same inversion trap the table-styling
 * rule documents for theme greys).
 */
const CHART_TEAL = '#4cceac';
const CHART_BLUE = '#6870fa';

/**
 * Unterhalb dieser Ordersumme wird KEINE Prozentspalte gezeigt. Bei 16 Orders
 * wäre „31 %“ Scheingenauigkeit — eine Order mehr oder weniger verschiebt den
 * Wert um 6 Punkte —, und genau dieser Block landet in Screenshots.
 */
const CHANNEL_SHARE_MIN_ORDERS = 50;

const STATUS_LABELS: Record<string, string> = {
  active: 'Aktiv (im Warmup)',
  clicked: 'Geklickt (bis Tag 14)',
  converted: 'Käufer (gesamt)',
  migrated_no_click: 'Migriert — kein Klick',
  migrated_no_purchase: 'Migriert — kein Kauf',
  migrated_suppressed: 'Unterdrückt (Blacklist)',
  excluded: 'Ausgeschlossen (Unsub)',
  excluded_ad_optout: 'Ausgeschlossen (Werbung abgewählt)',
};

const STATUS_HINTS: Record<string, string> = {
  converted:
    'Anmeldungen mit IRGENDEINER FTY-Order nach Anmeldung — auch über Newsletter, Webinar oder SAM. Nicht gleichbedeutend mit „durch die Welcome-Mails verkauft“; dafür siehe „Attribuierte Orders“ oben.',
  migrated_suppressed:
    'Auf der DNV-Blacklist und deshalb bewusst NICHT an DNV übergeben. Terminal, aber ohne DNV-Eintrag — zählt daher nicht zu den migrierten Nutzern.',
  excluded:
    'Vor Abschluss der Serie bei FT abgemeldet (Newsletter-Abmeldung) — keine DNV-Übergabe. Werbewiderspruch ohne Abmeldung zählt separat in der Kachel daneben.',
  excluded_ad_optout:
    'Hat der Werbung widersprochen (skip_sams), OHNE sich abzumelden — der Newsletter läuft weiter. Terminal, weil DNV genau diese Werbung verschickt: keine DNV-Übergabe, keine weiteren Welcome-Mails. Zählt NICHT als FT-Abmeldung.',
};

const EXPLAINER_SECTIONS: ExplainerSection[] = [
  {
    label: 'Datengrundlage',
    body: 'Alle Nutzer, die seit dem Start am 02.07.2026 in die Welcome-Serie aufgenommen wurden, plus jede Bestellung und jede Webinar-Anmeldung dieser Personen. Bestellungen zählen nur, wenn sie bei FT liegen, im Status PROCESSING sind und nicht storniert wurden.',
  },
  {
    label: 'Berechnung',
    body: 'Der obere Teil rechnet je Kampagne („hat dieses Mailing verkauft“), die Blöcke „Kanäle“ und „Webinar-Engagement“ rechnen je Person („wo kaufen diese Nutzer eigentlich“). Deshalb dürfen sich die Zahlen unterscheiden: eine über einen weitergeleiteten Welcome-Link ausgelöste Bestellung zählt als attribuierte Order, aber nicht als Kohorten-Order, weil der Käufer nie in der Serie war.',
  },
  {
    label: 'Aussage',
    body: 'Die Serie ist nur einer von mehreren Kanälen, über die ein frischer Anmelder kauft. Wer nur die attribuierten Orders liest, hält die Kohorte für kaufunwillig; der Kanal-Block zeigt, wie viel tatsächlich zusammenkommt und über welchen Weg.',
  },
  {
    label: 'Wichtig',
    highlight: true,
    body: 'Ausgeschlossen sind Zeilen, deren Profil nie aus einer FT-Anmeldung entstand, sondern aus einem Listen-Import — sie waren nie Zielgruppe und gehören auch nicht in den Nenner.',
  },
];

const fmt = (value: number): string => value.toLocaleString('de-DE');
/**
 * Feste Nachkommastellen in deutscher Schreibweise. `toFixed()` liefert einen
 * Dezimalpunkt — auf einer durchgehend deutschen Oberfläche liest sich „0.42 %“
 * wie ein Tippfehler, und bei Tausenderwerten wie eine andere Größenordnung.
 */
const dec = (value: number, digits: number): string =>
  value.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const pct = (part: number, whole: number): string =>
  whole > 0 ? `${dec((part / whole) * 100, 1)} %` : '—';
const eur = (value: number): string => `${dec(value, 2)} €`;
/** `YYYY-MM-DD` → `DD.MM.YYYY`. */
const de = (iso: string): string => iso.split('-').reverse().join('.');
/**
 * Letzter EINGESCHLOSSENER Tag eines halboffenen Fensters. Die Kontrollgruppe
 * filtert `signup_date < COMPARE_CONTROL_END`, der 01.07. gehört also NICHT
 * mehr dazu. Die rohe Obergrenze anzuzeigen benennt einen Tag, den die Abfrage
 * absichtlich ausschließt — und zwar in genau dem Satz, der die Gruppe definiert.
 */
const deInclusiveEnd = (isoExclusive: string): string => {
  const date = new Date(`${isoExclusive}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return de(date.toISOString().slice(0, 10));
};
/** Kaufquote — zwei Nachkommastellen, weil sie sich um 0,5 % bewegt. */
const rate = (part: number, whole: number): string =>
  whole > 0 ? `${dec((part / whole) * 100, 2)} %` : '—';
/** ETKP-Ist = CPA-Wert der Orders (je Anfo bewertet) / Empfänger × 1000; die Prognose rechnet mit dem Produkt-CPA. */
const etkpActual = (row: PerMailing): number =>
  row.recipients > 0 ? (Number(row.cpa_revenue || 0) / row.recipients) * 1000 : 0;

interface EvaluationTabProps {
  /**
   * `overview` = Reiter „Überblick" (läuft es, lohnt es sich — ~8 Zahlen),
   * `details`  = alles Übrige. Beide Ansichten teilen sich EINE Instanz und
   * damit EINEN Fetch; siehe Kommentar an der Einbindung in `index.tsx`.
   */
  view: 'overview' | 'details';
}

const EvaluationTab: FC<EvaluationTabProps> = ({ view }) => {
  const theme = useTheme();
  const [data, setData] = useState<EvaluationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const load = useCallback(async (start: string, end: string) => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (start) params.startDate = start;
      if (end) params.endDate = end;
      const response = await api.get('/api/welcome-campaign/evaluation', { params });
      setData(response.data.data);
    } catch (error) {
      console.error('Failed to load evaluation', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load('', '');
  }, [load]);

  const statusMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const row of data?.statusCounts ?? []) map[row.status] = row.count;
    return map;
  }, [data]);

  // PERSON-level funnel only. Send rows count EVENTS, not people — a batch that
  // failed and was re-sent counts twice (2.568 send events vs 2.410 enrollees
  // on 2026-07-29), so folding them in produced drop-offs like −11.686 %. The
  // per-mail send volumes live in the chips below, clearly labelled.
  // NUR die Engagement-Kette steht im Funnel, und ausschliesslich welcome-scoped.
  // „Gekauft", „Serie vollständig" und der qualifizierte Klick liegen als Karten
  // daneben — sie sind KEINE Teilmengen der Stufen darüber (gemessen 2026-08-05:
  // 7 der 14 Käufer haben nie geöffnet, der qualifizierte Klick zählt FT-weit).
  // Ein Funnel würde eine Verschachtelung suggerieren, die es nicht gibt.
  const stages = useMemo(() => {
    if (!data) return [];
    return [
      {
        label: 'Anmeldungen',
        value: data.funnel.enrolled,
        hint: 'Nutzer, die je in die Serie aufgenommen wurden',
      },
      {
        label: 'Versandt',
        value: data.funnel.sent,
        hint: 'Mindestens eine Welcome-Mail nachweislich rausgegangen (per-Empfänger-Spiegel). Bewusst „versandt“ und nicht „zugestellt“: die Quelle ist Iterables emailSend, Bounces sind darin enthalten.',
      },
      {
        label: 'Geöffnet',
        value: data.funnel.opened,
        hint: 'Hat mindestens eine Welcome-Mail geöffnet. Untergrenze — Apple Mail Privacy und Bildblocker unterdrücken das Zählpixel.',
      },
      {
        label: 'Geklickt',
        value: data.funnel.clicked_welcome,
        hint: 'Hat in einer Welcome-Mail geklickt. Nicht alle davon stehen in „Geöffnet“ — ohne getracktes Zählpixel gibt es einen Klick ohne Öffnung.',
      },
    ];
  }, [data]);

  // Kennzahlen, die neben dem Funnel stehen, weil sie sich nicht in ihn einreihen
  // lassen — jeweils mit dem Grund, warum nicht.
  const funnelAsideCards = useMemo(() => {
    if (!data) return [];
    return [
      {
        label: 'Käufer (gesamt)',
        value: data.funnel.converted,
        hint: `${STATUS_HINTS.converted} — gezählt wird JEDE FTY-Bestellung nach der Anmeldung, gleich über welchen Kanal, nicht nur welcome-attribuierte (dafür gibt es die eigene Kennzahl „Attribuierte Orders“). Steht deshalb neben dem Funnel und nicht darin: ${dec(data.funnel.converted_without_open, 0)} der Käufer haben nie eine Welcome-Mail geöffnet.`,
      },
      {
        label: 'Qualifizierter Klick',
        value: data.funnel.clicked_qualified,
        hint: 'Klick auf einen attribuierbaren Link (banner=/affiliate=), FT-weit — nicht nur in Welcome-Mails. Gelesen aus `clicked_at`, das die Migration überlebt; der Status tut das nicht.',
      },
      {
        label: 'Serie vollständig',
        value: data.funnel.completed_series,
        hint: 'Serie komplett erhalten (final_mailing_sent_at gesetzt). Misst Laufzeit, nicht Konversion — der Rest ist aktiv, im Warmup oder vorher ausgestiegen.',
      },
    ];
  }, [data]);

  const barData = useMemo(
    () =>
      (data?.perMailing ?? []).map((row) => ({
        mailing: `#${row.mailing_id}`,
        Öffnungsrate:
          row.recipients > 0 ? +((row.unique_opens / row.recipients) * 100).toFixed(2) : 0,
        Klickrate:
          row.recipients > 0 ? +((row.unique_clicks / row.recipients) * 100).toFixed(2) : 0,
        'Orders je 1.000':
          row.recipients > 0 ? +((row.orders / row.recipients) * 1000).toFixed(2) : 0,
      })),
    [data],
  );

  const scatterData = useMemo(
    () =>
      (data?.perMailing ?? [])
        .filter((row) => row.etkp_forecast !== null)
        .map((row) => ({
          x: Number(row.etkp_forecast),
          y: +etkpActual(row).toFixed(2),
          z: row.orders,
          mailing: `#${row.mailing_id}`,
          subject: row.subject,
          orders: row.orders,
        })),
    [data],
  );

  const channelTotals = useMemo(() => {
    const rows = data?.channels ?? [];
    return {
      orders: rows.reduce((sum, row) => sum + row.orders, 0),
      cpa_revenue: rows.reduce((sum, row) => sum + row.cpa_revenue, 0),
    };
  }, [data]);

  // Nivo zeichnet horizontal von unten nach oben — ohne das Umdrehen stünde der
  // stärkste Kanal ganz unten und der Block läse sich rückwärts.
  const channelBarData = useMemo(
    () =>
      [...(data?.channels ?? [])]
        .sort((a, b) => a.orders - b.orders)
        .map((row) => ({ kanal: row.label, Orders: row.orders })),
    [data],
  );

  // Orders sind ganzzahlig. Ohne feste Ticks beschriftet Nivo die Achse bei
  // kleinen Werten in Halbschritten („0,5 Orders“), was eine Genauigkeit
  // suggeriert, die es nicht gibt.
  const channelTickValues = useMemo(() => {
    const max = Math.max(0, ...channelBarData.map((row) => row.Orders));
    const step = Math.max(1, Math.ceil(max / 8));
    const ticks: number[] = [];
    for (let value = 0; value <= max; value += step) ticks.push(value);
    return ticks;
  }, [channelBarData]);

  const scatterMax = useMemo(
    () => Math.ceil(Math.max(10, ...scatterData.flatMap((p) => [p.x, p.y])) * 1.1),
    [scatterData],
  );

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (!data) {
    return <Alert severity="error">Auswertung konnte nicht geladen werden.</Alert>;
  }

  const { totals } = data;
  const maxStage = Math.max(1, ...stages.map((s) => s.value));

  return (
    <Box>
      <Box display="flex" gap={2} alignItems="center" mb={2} flexWrap="wrap">
        <TextField
          size="small"
          type="date"
          label="Von"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label="Bis"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
        />
        <Button variant="contained" onClick={() => load(startDate, endDate)} disabled={loading}>
          Anwenden
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setStartDate('');
            setEndDate('');
            load('', '');
          }}
          disabled={loading || (!startDate && !endDate)}
        >
          Gesamt
        </Button>
        {loading && <CircularProgress size={20} />}
        {!startDate && !endDate && (
          <Chip size="small" variant="outlined" label="Zeitraum: Gesamt (seit Start)" />
        )}
      </Box>

      <Explainer title="Wie diese Auswertung zu lesen ist" sections={EXPLAINER_SECTIONS} />

      {view === 'overview' && (
        <>
          {/* ---- Betrieb ---- */}
          <Typography variant="h5" sx={{ mb: 1 }}>
            Betrieb
          </Typography>
          <Alert severity={data.operations.ok ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {data.operations.ok ? (
              <>
                Läuft. In den letzten 24 h gingen {fmt(data.operations.sent_24h)} Mails aus{' '}
                {fmt(data.operations.campaigns_24h)} Kampagnen raus, keine Fehlschläge, keine
                hängenden Kampagnen.
              </>
            ) : (
              <>
                <strong>Nachsehen.</strong>{' '}
                {data.operations.sent_24h === 0 && 'Seit über 24 h ging kein Welcome-Mail raus. '}
                {data.operations.failed_24h > 0 &&
                  `${fmt(data.operations.failed_24h)} Kampagne(n) in den letzten 24 h fehlgeschlagen. `}
                {data.operations.stuck_open > 0 &&
                  `${fmt(data.operations.stuck_open)} Kampagne(n) hängen seit über 2 h in einem Zwischenzustand — sie sind weder versandt noch fehlgeschlagen, die Empfänger warten. `}
                <br />
                Versand 24 h: {fmt(data.operations.sent_24h)} · Fehlschläge insgesamt seit Start:{' '}
                {fmt(data.operations.failed_total)} (historisch, steuert diese Meldung nicht).
              </>
            )}
          </Alert>

          {/* ---- Käufe der Kohorte ---- */}
          <Typography variant="h5" sx={{ mb: 1, mt: 3 }}>
            Käufe der Kohorte
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" mb={1}>
            {[
              {
                label: 'aus der Welcome-Serie',
                // `funnel.converted_via_welcome`, NICHT `totals.attributed_orders`:
                // nur diese Zahl teilt den Anker (`enrolled_at`) mit den beiden
                // anderen Kacheln. Mit der order-datums-verankerten Zahl wurde die
                // Differenz am 23.07. negativ (attribuiert 1, converted 0).
                value: fmt(data.funnel.converted_via_welcome),
                hint: 'Kohortenmitglieder, deren Kauf über den Welcome-Banner dieser Serie zugeordnet ist — der Ertrag der Serie selbst. Weicht bewusst von „Attribuierte Orders“ im Details-Reiter ab: dort werden ORDERS gezählt (nach Orderdatum, auch von Käufern ausserhalb der sichtbaren Kohorte), hier PERSONEN der Kohorte nach Anmeldedatum. Beide sind richtig, sie beantworten verschiedene Fragen.',
              },
              {
                label: 'über andere Kanäle',
                value: fmt(data.funnel.converted - data.funnel.converted_via_welcome),
                hint: 'Kohortenmitglieder, die nach ihrer Anmeldung bei FT gekauft haben, aber über einen anderen Weg: DOI-SAM, Newsletter, Webinar, Redlink, Fremdportal. Nicht der Serie zuzurechnen — aber es zeigt, dass die Kohorte kauft.',
              },
              {
                label: 'Käufer insgesamt',
                value: fmt(data.funnel.converted),
                hint: 'Alle Kohortenmitglieder mit mindestens einer FTY-Bestellung nach Anmeldung, gleich über welchen Kanal. Summe der beiden Zahlen links.',
              },
              {
                label: 'CPA-Erlös (Serie)',
                // `cpa_revenue_via_welcome`, NICHT `totals.cpa_revenue`: nur diese
                // Summe teilt den Anker (`enrolled_at`) mit den drei Kacheln links.
                // Mit der order-verankerten Summe zeigte die Gruppe am 23.07.
                // „Serie 0 Orders" neben „68,59 € Erlös".
                value: `${dec(data.funnel.cpa_revenue_via_welcome, 2)} €`,
                hint: 'Erlös aus den Orders der Kohortenmitglieder, die über die Serie gekauft haben — auf dasselbe Anmeldedatum verankert wie die drei Zahlen links. Die Kachel „CPA-Erlös“ im Details-Reiter rechnet nach Orderdatum und kann deshalb abweichen.',
              },
            ].map((card) => (
              <Tooltip key={card.label} title={card.hint} arrow>
                <Box
                  sx={{
                    border: '1px solid rgba(128,128,128,0.4)',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    minWidth: 170,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h6">{card.value}</Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>

          {/* ---- Einordnung ---- */}
          <Typography variant="h5" sx={{ mb: 1, mt: 3 }}>
            Einordnung{' '}
            <Chip
              size="small"
              variant="outlined"
              color="info"
              label="Gesamtzeitraum — folgt dem Zeitfilter NICHT"
              sx={{ ml: 1, verticalAlign: 'middle' }}
            />
          </Typography>
          {data.benchmark.factor !== null ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Die Serie erzeugt <strong>{dec(data.benchmark.welcome_rate_pct ?? 0, 3)} %</strong>{' '}
              Orders je Nachricht, die manuellen PLX-Direktmailings an dieselbe FT-Liste{' '}
              <strong>{dec(data.benchmark.direct_rate_pct ?? 0, 3)} %</strong> — Faktor{' '}
              <strong>{dec(data.benchmark.factor, 1)}×</strong>.
              <br />
              Basis seit dem ersten Welcome-Versand
              {data.benchmark.window_start
                ? ` (${new Date(data.benchmark.window_start).toLocaleDateString('de-DE')})`
                : ''}
              : {fmt(data.benchmark.welcome_messages)} Nachrichten /{' '}
              {fmt(data.benchmark.welcome_orders)} Orders gegen{' '}
              {fmt(data.benchmark.direct_messages)} / {fmt(data.benchmark.direct_orders)}.
              <br />
              <em>
                Hier stehen <strong>alle</strong> Welcome-Orders (
                {fmt(data.benchmark.welcome_orders)}) gegen <strong>alle</strong> versandten
                Nachrichten — nicht auf die sichtbare Kohorte beschränkt, weil Zähler und Nenner
                dieselbe Grundgesamtheit haben müssen. Die Kachel „aus der Welcome-Serie“ oben zählt
                dagegen <strong>Personen der Kohorte</strong> (
                {fmt(data.funnel.converted_via_welcome)}). Beide Zahlen sind richtig, sie zählen
                Verschiedenes.
              </em>
              <br />
              <strong>
                Bei {fmt(data.benchmark.welcome_orders)} gegen {fmt(data.benchmark.direct_orders)}{' '}
                Orders ist die Richtung konsistent, das Verhältnis aber nicht belastbar.
              </strong>{' '}
              Und der Vergleich ist konfundiert: die Serie geht an frische Anmelder, die
              Direktmailings an die ganze Liste — diese Zielgruppe klickt auf identische Copy
              messbar besser. Die Zahl beantwortet „wohin schicke ich die nächste Nachricht“, nicht
              „das Serienformat wirkt“.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Noch keine Vergleichsbasis — es fehlen Orders auf mindestens einer Seite.
            </Alert>
          )}
        </>
      )}

      {view === 'details' && (
        <>
          {/* ---- (b) Gesamt ---- */}
          <Typography variant="h5" sx={{ mb: 1 }}>
            Gesamt — alle Welcome-Sendungen
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" mb={1}>
            {[
              { label: 'Kampagnen', value: fmt(totals.campaigns) },
              { label: 'Versendet', value: fmt(totals.recipients) },
              {
                label: 'Öffnungen (eindeutig)',
                value: `${fmt(totals.unique_opens)} · ${pct(totals.unique_opens, totals.recipients)}`,
              },
              {
                label: 'Klicks (eindeutig)',
                value: `${fmt(totals.unique_clicks)} · ${pct(totals.unique_clicks, totals.recipients)}`,
              },
              {
                label: 'Attribuierte Orders',
                value: fmt(totals.attributed_orders),
                hint: 'Nur Orders mit Welcome-Banner dieser Kampagne — der direkt zurechenbare Umsatz.',
              },
              {
                label: 'CPA-Erlös',
                value: eur(totals.cpa_revenue),
                hint: 'Jede Order je Anfo bewertet (eigener CPA-Wert) — die ETKP-Prognose des Pickers rechnet dagegen zum heutigen CPA-Wert des Produkts.',
              },
              {
                label: 'ETKP-Ist',
                value:
                  totals.recipients > 0
                    ? `${dec((totals.cpa_revenue / totals.recipients) * 1000, 2)} €`
                    : '—',
              },
            ].map((card) => (
              <Tooltip key={card.label} title={card.hint ?? ''} arrow>
                <Box
                  sx={{
                    border: '1px solid rgba(128,128,128,0.4)',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    minWidth: 150,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h6">{card.value}</Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
          {totals.missing_metrics > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {totals.missing_metrics} Kampagne(n) ohne Metrik-Daten im Iterable-Cache — Öffnungen
              und Klicks sind entsprechend leicht untererfasst.
            </Alert>
          )}

          {/* ---- (c) Funnel ---- */}
          <Typography variant="h5" sx={{ mb: 1, mt: 3 }}>
            Funnel — Versand und Reaktion
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Nur Welcome-Mails: jede Stufe zählt Personen, die in einer Mail <em>dieser Serie</em>
            reagiert haben. Die Karten darunter stehen bewusst daneben und nicht im Funnel — sie
            sind keine Teilmengen der Stufen.
          </Typography>
          <Box mb={2}>
            {stages.map((stage, index) => {
              const previous = index > 0 ? stages[index - 1].value : null;
              const drop = previous && previous > 0 ? (1 - stage.value / previous) * 100 : null;
              const share =
                data.funnel.enrolled > 0 ? (stage.value / data.funnel.enrolled) * 100 : 0;
              return (
                <Tooltip key={stage.label} title={stage.hint} arrow placement="right">
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="body2" sx={{ minWidth: 175 }}>
                      {stage.label}
                    </Typography>
                    <Box
                      sx={{
                        height: 26,
                        width: `${Math.max(2, (stage.value / maxStage) * 100)}%`,
                        bgcolor: CHART_TEAL,
                        opacity: 0.45 + 0.55 * (stage.value / maxStage),
                        borderRadius: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        pr: 1,
                        minWidth: 60,
                      }}
                    >
                      <Typography variant="caption" fontWeight={600} sx={{ color: '#141414' }}>
                        {fmt(stage.value)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {dec(share, 1)} % der Anmeldungen
                      {drop !== null && drop > 0 ? ` · −${dec(drop, 1)} % ggü. Schritt davor` : ''}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          {data.funnel.clicked_without_open > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {fmt(data.funnel.clicked_without_open)} Person(en) haben geklickt, ohne dass eine
              Öffnung getrackt wurde — Apple Mail Privacy und Bildblocker unterdrücken das
              Zählpixel. Die Stufen sind deshalb <strong>keine</strong> exakten Teilmengen
              voneinander, „Geöffnet“ ist eine Untergrenze.
            </Alert>
          )}

          <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
            {funnelAsideCards.map((card) => (
              <Tooltip key={card.label} title={card.hint} arrow>
                <Box
                  sx={{
                    border: '1px solid rgba(128,128,128,0.4)',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    minWidth: 150,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h6">{fmt(card.value)}</Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>

          <Box display="flex" gap={2} flexWrap="wrap" mb={1}>
            {Object.entries(STATUS_LABELS).map(([status, label]) => (
              <Tooltip key={status} title={STATUS_HINTS[status] ?? ''} arrow>
                <Box
                  sx={{
                    border: '1px solid rgba(128,128,128,0.4)',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    minWidth: 150,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="h5">{fmt(statusMap[status] ?? 0)}</Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
            {data.sendStats.map((stat) => (
              <Chip
                key={stat.mailing_position}
                variant="outlined"
                size="small"
                label={`Mail #${stat.mailing_position}: ${fmt(stat.campaigns)} Kampagnen · ${fmt(stat.recipients)} versendet${stat.failed > 0 ? ` · ${stat.failed} fehlgeschlagen` : ''}${stat.pending > 0 ? ` · ${stat.pending} offen` : ''}`}
              />
            ))}
            {data.migratedTotal > 0 && (
              <Tooltip
                arrow
                title="Migrierte Nutzer, die NACH der DNV-Migration noch bei FT gekauft haben (über den Newsletter, den sie behalten). Ein deutlicher Wert heißt: das Gate migriert zu früh — Klick-/Kauf-Fenster prüfen."
              >
                <Chip
                  size="small"
                  color={data.lateBuyers?.late_buyers > 0 ? 'warning' : 'default'}
                  label={`Späte Käufer nach Migration: ${fmt(data.lateBuyers?.late_buyers ?? 0)} (${fmt(data.lateBuyers?.late_orders ?? 0)} Orders) von ${fmt(data.migratedTotal)} migrierten`}
                />
              </Tooltip>
            )}
          </Box>

          {/* ---- A/B-Test Mail 1 ---- */}
          {/*
            Eingeklappt, NICHT entfernt: der Block wird derzeit nicht genutzt,
            aber ein verlorener A/B-Readout ist schwerer wiederherzustellen als
            ein Klick zum Aufklappen.
          */}
          {data.abTest && (
            <Accordion sx={{ mt: 3 }} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5">A/B-Test Mail 1</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity={data.abTest.significant ? 'success' : 'info'} sx={{ mb: 2 }}>
                  Entschieden wird auf der <strong>Klickrate</strong> — bei diesem Anmelde-Volumen
                  ist ein 40-%-Lift der Klickrate in etwa zwei Monaten nachweisbar, auf Orders
                  entschieden wären es rund zwei Jahre. Orders laufen deshalb nur als Richtungsgröße
                  mit.
                  {!data.abTest.decidable && (
                    <>
                      {' '}
                      <strong>
                        Noch nicht aussagekräftig: mindestens {fmt(data.abTest.minPerArm)}{' '}
                        versendete Mails je Arm nötig.
                      </strong>
                    </>
                  )}
                </Alert>
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Arm</TableCell>
                        <TableCell align="right">Versendet</TableCell>
                        <TableCell align="right">Öffnungsrate</TableCell>
                        <TableCell align="right">Klickrate</TableCell>
                        <TableCell align="right">Orders</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[
                        { label: 'A — automatisch gewählt', row: data.abTest.auto },
                        { label: 'B — eigene Welcome-Copy', row: data.abTest.custom },
                      ].map((entry) => (
                        <TableRow key={entry.label} hover>
                          <TableCell>{entry.label}</TableCell>
                          <TableCell align="right">{fmt(entry.row.recipients)}</TableCell>
                          <TableCell align="right">
                            {pct(entry.row.unique_opens, entry.row.recipients)}
                          </TableCell>
                          <TableCell align="right">
                            <strong>{pct(entry.row.unique_clicks, entry.row.recipients)}</strong>
                          </TableCell>
                          <TableCell align="right">{fmt(entry.row.orders)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Differenz {dec(data.abTest.diff * 100, 2)} Prozentpunkte
                  {data.abTest.pValue !== null && ` · p = ${dec(data.abTest.pValue, 3)}`}
                  {data.abTest.ci95Low !== null &&
                    data.abTest.ci95High !== null &&
                    ` · 95 %-Intervall ${dec(data.abTest.ci95Low * 100, 2)} bis ${dec(data.abTest.ci95High * 100, 2)} pp`}
                  {data.abTest.nRequiredPerArm !== null &&
                    ` · für eine belastbare Aussage nötig: ~${fmt(data.abTest.nRequiredPerArm)} je Arm`}
                  .{' '}
                  {data.abTest.significant
                    ? 'Der Unterschied ist statistisch abgesichert — die Umstellung bleibt trotzdem eine bewusste manuelle Entscheidung.'
                    : 'Kein abgesicherter Unterschied. Es wird nichts automatisch umgestellt.'}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {/* ---- (a) Pro Mailing ---- */}
          <Typography variant="h5" sx={{ mb: 1 }}>
            Pro Mailing — Ist vs. Prognose
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>ETKP-Prognose</strong> ist der Wert, mit dem der Picker das Mailing ausgewählt
            hat (CPA-ETKP der letzten 6 Monate über alle FT-PLX-Sendungen) — umgerechnet auf den
            heutigen CPA-Wert des Produkts (der wird monatlich neu gerechnet). Mit * markiert:
            Wochen ohne Orders, dort ließ sich nicht umrechnen.{' '}
            <strong>ETKP-Ist</strong> ist der CPA-Wert je 1.000 Empfänger des Welcome-Versands
            allein, jede Order je Anfo bewertet (wie die Gesamtsumme) — die Prognose rechnet
            dagegen zum CPA-Wert des Produkts. Bei wenigen Orders je Mailing ist der Ist-Wert sehr volatil — die Order-Spalte zeigt die
            Fallzahl dahinter.
          </Alert>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Mailing</TableCell>
                  <TableCell>Betreff</TableCell>
                  <TableCell>Produkt</TableCell>
                  <TableCell>Pos.</TableCell>
                  <TableCell align="right">Versendet</TableCell>
                  <TableCell align="right">Öffnungsrate</TableCell>
                  <TableCell align="right">Klickrate</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell align="right">ETKP-Ist</TableCell>
                  <TableCell align="right">ETKP-Prognose</TableCell>
                  <TableCell align="right">Δ</TableCell>
                  <TableCell align="right">Wochen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.perMailing.map((row) => {
                  const actual = etkpActual(row);
                  const forecast = row.etkp_forecast;
                  const delta =
                    forecast !== null && forecast > 0 ? (actual / forecast - 1) * 100 : null;
                  return (
                    <TableRow key={row.mailing_id} hover>
                      <TableCell>#{row.mailing_id}</TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" noWrap title={row.subject}>
                          {row.subject}
                        </Typography>
                        {row.ai_topic && (
                          <Typography variant="caption" color="text.secondary">
                            {row.ai_topic}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{row.product_code}</TableCell>
                      <TableCell>{row.positions}</TableCell>
                      <TableCell align="right">{fmt(row.recipients)}</TableCell>
                      <TableCell align="right">{pct(row.unique_opens, row.recipients)}</TableCell>
                      <TableCell align="right">{pct(row.unique_clicks, row.recipients)}</TableCell>
                      <TableCell align="right">{fmt(row.orders)}</TableCell>
                      <TableCell align="right">{dec(actual, 2)} €</TableCell>
                      <TableCell align="right">
                        {forecast !== null ? `${dec(forecast, 2)} €` : '—'}
                        {(row.forecast_unscaled_weeks ?? 0) > 0 && (
                          <span title="Mindestens eine Auswahlwoche ohne Orders — nicht auf den heutigen CPA umgerechnet">
                            *
                          </span>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {delta !== null ? (
                          <Typography
                            variant="body2"
                            color={delta >= 0 ? 'success.main' : 'error.main'}
                          >
                            {delta >= 0 ? '+' : ''}
                            {dec(delta, 0)} %
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">{row.weeks_selected}</TableCell>
                    </TableRow>
                  );
                })}
                {data.perMailing.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      Keine Sendungen im gewählten Zeitraum.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {barData.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Mailing-Vergleich
              </Typography>
              <Box height={320} mb={4}>
                <ResponsiveBar
                  data={barData}
                  keys={['Öffnungsrate', 'Klickrate', 'Orders je 1.000']}
                  indexBy="mailing"
                  groupMode="grouped"
                  margin={{ top: 10, right: 140, bottom: 50, left: 60 }}
                  padding={0.25}
                  colors={{ scheme: 'nivo' }}
                  axisBottom={{ legend: 'Mailing', legendPosition: 'middle', legendOffset: 38 }}
                  axisLeft={{
                    legend: '% bzw. Orders je 1.000',
                    legendPosition: 'middle',
                    legendOffset: -50,
                  }}
                  theme={{
                    text: { fill: theme.palette.text.primary },
                    axis: {
                      ticks: { text: { fill: theme.palette.text.secondary } },
                      legend: { text: { fill: theme.palette.text.primary } },
                    },
                    grid: { line: { stroke: 'rgba(128,128,128,0.2)' } },
                    tooltip: { container: { background: theme.palette.background.paper } },
                  }}
                  legends={[
                    {
                      dataFrom: 'keys',
                      anchor: 'right',
                      direction: 'column',
                      translateX: 130,
                      itemWidth: 120,
                      itemHeight: 20,
                      symbolSize: 12,
                    },
                  ]}
                />
              </Box>
            </>
          )}

          {scatterData.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Prognose vs. Ist
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Punkte auf der Diagonale = der Picker hat richtig vorhergesagt. Oberhalb = das
                Mailing lief besser als prognostiziert, unterhalb schlechter. Punktgröße = Anzahl
                Orders; Mailings ohne Order liegen zwangsläufig auf y = 0 und sagen wenig aus.
              </Typography>
              <Box height={340} mb={2}>
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 10, right: 20, bottom: 40, left: 10 }}>
                    <CartesianGrid stroke="rgba(128,128,128,0.2)" />
                    <XAxis
                      type="number"
                      dataKey="x"
                      name="ETKP-Prognose"
                      unit=" €"
                      domain={[0, scatterMax]}
                      stroke={theme.palette.text.secondary}
                      label={{
                        value: 'ETKP-Prognose (Picker)',
                        position: 'insideBottom',
                        offset: -25,
                        fill: theme.palette.text.primary,
                      }}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      name="ETKP-Ist"
                      unit=" €"
                      domain={[0, scatterMax]}
                      stroke={theme.palette.text.secondary}
                      label={{
                        value: 'ETKP-Ist',
                        angle: -90,
                        position: 'insideLeft',
                        fill: theme.palette.text.primary,
                      }}
                    />
                    <ZAxis type="number" dataKey="z" range={[80, 400]} name="Orders" />
                    <ReferenceLine
                      segment={[
                        { x: 0, y: 0 },
                        { x: scatterMax, y: scatterMax },
                      ]}
                      stroke={theme.palette.text.secondary}
                      strokeDasharray="4 4"
                    />
                    <RTooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      contentStyle={{
                        background: theme.palette.background.paper,
                        border: '1px solid rgba(128,128,128,0.4)',
                      }}
                      formatter={(value: any, name: string) => [
                        `${value} ${name === 'Orders' ? '' : '€'}`,
                        name,
                      ]}
                      labelFormatter={() => ''}
                      content={({ payload }: any) => {
                        const point = payload?.[0]?.payload;
                        if (!point) return null;
                        return (
                          <Box
                            sx={{
                              background: theme.palette.background.paper,
                              border: '1px solid rgba(128,128,128,0.4)',
                              borderRadius: 1,
                              p: 1,
                              maxWidth: 320,
                            }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {point.mailing} — {point.subject}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Prognose {dec(point.x, 2)} € · Ist {dec(point.y, 2)} €
                            </Typography>
                            <Typography variant="caption" display="block">
                              {point.orders} Order(s)
                            </Typography>
                          </Box>
                        );
                      }}
                    />
                    <Legend verticalAlign="top" height={24} />
                    <Scatter name="Mailings" data={scatterData} fill={CHART_BLUE} />
                  </ScatterChart>
                </ResponsiveContainer>
              </Box>
            </>
          )}

          {/* ---- (d) Kanäle ---- */}
          <Typography variant="h5" sx={{ mb: 1, mt: 3 }}>
            Kanäle — wo die Kohorte kauft
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Alle Bestellungen der Kohorte, aufgeschlüsselt nach dem Kanal, über den sie ausgelöst
            wurden — nicht nur die der Serie. Der Block dreht damit die Frage der Tabelle darüber
            um: dort geht es um „hat dieses Mailing verkauft“, hier um „wo kaufen diese Nutzer“.
            Stichtag ist wie oben das Bestelldatum.
          </Alert>
          <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
            {[
              { label: 'Orders der Kohorte', value: fmt(channelTotals.orders) },
              { label: 'CPA-Erlös gesamt', value: eur(channelTotals.cpa_revenue) },
            ].map((card) => (
              <Box
                key={card.label}
                sx={{
                  border: '1px solid rgba(128,128,128,0.4)',
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  minWidth: 170,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {card.label}
                </Typography>
                <Typography variant="h6">{card.value}</Typography>
              </Box>
            ))}
          </Box>
          {channelBarData.length > 0 && (
            <Box height={Math.max(220, channelBarData.length * 38 + 60)} mb={2}>
              <ResponsiveBar
                data={channelBarData}
                keys={['Orders']}
                indexBy="kanal"
                layout="horizontal"
                margin={{ top: 5, right: 30, bottom: 45, left: 130 }}
                padding={0.3}
                colors={[CHART_TEAL]}
                labelSkipWidth={18}
                gridXValues={channelTickValues}
                axisBottom={{
                  legend: 'Orders',
                  legendPosition: 'middle',
                  legendOffset: 34,
                  tickValues: channelTickValues,
                }}
                theme={{
                  text: { fill: theme.palette.text.primary },
                  axis: {
                    ticks: { text: { fill: theme.palette.text.secondary } },
                    legend: { text: { fill: theme.palette.text.primary } },
                  },
                  grid: { line: { stroke: 'rgba(128,128,128,0.2)' } },
                  tooltip: { container: { background: theme.palette.background.paper } },
                }}
              />
            </Box>
          )}
          <TableContainer sx={{ mb: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Kanal</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  {channelTotals.orders >= CHANNEL_SHARE_MIN_ORDERS && (
                    <TableCell align="right">Anteil</TableCell>
                  )}
                  <TableCell align="right">
                    Käufer
                    <InfoTip text="Verschiedene Personen. Liegt unter den Orders, wenn jemand mehrfach gekauft hat." />
                  </TableCell>
                  <TableCell align="right">
                    CPA-Erlös
                    <InfoTip text="Jede Order je Anfo bewertet (wie die Gesamtsumme) — die ETKP-Prognose des Pickers rechnet dagegen zum heutigen CPA-Wert des Produkts. Kein Zahlungseingang: junge Bestellungen tragen noch den Wert ihres Angebots." />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.channels ?? []).map((row) => (
                  <TableRow key={row.key} hover>
                    <TableCell>
                      {row.label}
                      {row.key === 'welcome' && (
                        <InfoTip text="Kohortenmitglied hat über einen Welcome-Link gekauft. Nicht identisch mit den attribuierten Orders oben: die zählen jeden Kauf über einen Welcome-Link, auch von jemandem, der die Mail nie bekommen hat (weitergeleiteter Link)." />
                      )}
                      {row.key === 'direct_mailing' && row.orders === 0 && (
                        <InfoTip text="Bis zum 30.07.2026 hat die Serie Direkt-Mailings an frische Anmelder blockiert — die 0 heißt also nicht, dass der Kanal bei Neuanmeldern nicht funktioniert. Seither tritt die Serie stattdessen zur Seite und die Zeile beginnt sich zu füllen." />
                      )}
                      {row.key === 'unknown' && (
                        <InfoTip text="Bestellung mit Banner, auf das keine Kanal-Regel passt. Taucht diese Zeile auf, ist die Kanal-Zuordnung (helpers/orderChannel.ts) unvollständig und gehört ergänzt." />
                      )}
                      {row.key === 'no_banner' && (
                        <InfoTip text="Bestellung ohne Banner und ohne affiliate-Kennung in der URL — meist direkt auf der Bestellseite gelandet. Kein Zuordnungsfehler." />
                      )}
                    </TableCell>
                    <TableCell align="right">{fmt(row.orders)}</TableCell>
                    {channelTotals.orders >= CHANNEL_SHARE_MIN_ORDERS && (
                      <TableCell align="right">{pct(row.orders, channelTotals.orders)}</TableCell>
                    )}
                    <TableCell align="right">{fmt(row.buyers)}</TableCell>
                    <TableCell align="right">{eur(row.cpa_revenue)}</TableCell>
                  </TableRow>
                ))}
                {(data.channels ?? []).length === 0 && (
                  <TableRow>
                    {/* Spaltenzahl mitziehen — die Anteil-Spalte ist bedingt. */}
                    <TableCell
                      colSpan={channelTotals.orders >= CHANNEL_SHARE_MIN_ORDERS ? 5 : 4}
                      align="center"
                    >
                      Keine Bestellungen im gewählten Zeitraum.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {channelTotals.orders < CHANNEL_SHARE_MIN_ORDERS && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Kleine Fallzahl — Anteile werden erst ab {CHANNEL_SHARE_MIN_ORDERS} Orders gezeigt.
              Bei derzeit {fmt(channelTotals.orders)} Orders verschiebt eine einzelne Bestellung den
              Anteil um mehrere Prozentpunkte.
            </Typography>
          )}

          {/* ---- (e) Webinar-Engagement ---- */}
          <Typography variant="h5" sx={{ mb: 1, mt: 3 }}>
            Webinar-Engagement der Kohorte
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Gezählt werden ausschließlich Webinar-Anmeldungen{' '}
            <strong>ab dem eigenen Anmeldetag</strong>, also das, was nach dem Eintritt in die Serie
            passiert ist. Frühere Anmeldungen bleiben bewusst draußen: ein Großteil der Kohorte ist
            überhaupt erst über das Webinar eines fremden Publishers zu einem FT-Profil gekommen —
            diese Registrierung ging der Anmeldung nicht voraus, sie hat sie ausgelöst. Als
            Engagement gezählt würde sie die Wirkungsrichtung umdrehen.
          </Alert>
          <Box display="flex" gap={2} flexWrap="wrap" mb={1}>
            {[
              // „Anmeldung" heisst auf diesem Bildschirm zweierlei: im Funnel oben die
              // Aufnahme in die Serie (`funnel.enrolled`), hier die Anmeldung zu einem
              // Webinar. Deshalb tragen DIESE Karten das Wort „Webinar" ausgeschrieben —
              // ein erklärender Satz im Explainer kann die Doppeldeutigkeit nicht
              // auflösen, er könnte sich nur für eine der beiden Seiten entscheiden.
              {
                label: 'Erreicht (Webinar-Mail)',
                value: fmt(data.webinarEngagement.reached),
                hint: `Kohortenmitglieder, die nach ihrer Anmeldung nachweislich mindestens eine Webinar-Mail bekommen haben — von ${fmt(data.webinarEngagement.cohort)} insgesamt. Das ist die Bezugsgröße: nur wer eine Mail bekam, konnte sich darüber anmelden.`,
              },
              {
                label: 'Personen mit Webinar-Anmeldung',
                value: fmt(data.webinarEngagement.registrants),
                hint: 'Verschiedene Personen aus der Kohorte mit mindestens einer Webinar-Anmeldung nach ihrem Anmeldetag. Zeitfenster-Anker ist das Anmeldedatum zum Webinar — diese Karte beantwortet „was ist in diesem Fenster passiert“.',
              },
              {
                // Zähler MUSS `registrants_cohort` sein, nicht `registrants`: der
                // Nenner `reached` ankert auf `enrolled_at`, `registrants` dagegen
                // auf `added_at`. Unter einem Zeitfenster sind das verschiedene
                // Grundgesamtheiten (01.–15.07.: 9 gegen 23) und die Quote wäre um
                // Faktor 2,5 falsch.
                label: 'Reaktionsquote (Anmeldung je Erreichtem)',
                value:
                  data.webinarEngagement.reached > 0
                    ? `${pct(data.webinarEngagement.registrants_cohort, data.webinarEngagement.reached)} · ${fmt(data.webinarEngagement.registrants_cohort)} von ${fmt(data.webinarEngagement.reached)}`
                    : '—',
                hint: 'Von den Kohortenmitgliedern, die nach ihrer Anmeldung eine Webinar-Mail bekamen: wie viele haben sich danach zu einem Webinar angemeldet. Zähler und Nenner ankern beide auf dem Serien-Anmeldedatum, sind unter jedem Zeitfenster also dieselbe Grundgesamtheit. Vorbehalt: die Webinar-Anmeldung muss nach dem eigenen Anmeldetag liegen, nicht zwingend nach der Mail — Näherung der Reaktion, keine exakte Expositionsquote.',
              },
              {
                label: 'Webinar-Anmeldungen',
                value: fmt(data.webinarEngagement.registrations),
                hint: 'Anmeldevorgänge insgesamt — eine Person kann sich für mehrere Webinare anmelden.',
              },
              {
                label: 'Teilnehmer',
                value: fmt(data.webinarEngagement.attendees),
                hint: 'Personen, die live im Webinar waren (mindestens ein Präsenz-Signal von StealthSeminar, je Webinar und E-Mail einmal gezählt) — dieselbe Definition wie auf /webinar-auswertungen.',
              },
              {
                label: 'No-Shows',
                value: fmt(data.webinarEngagement.no_shows),
                hint: 'Angemeldet, aber laut StealthSeminar nicht erschienen („missed“). Kann sich mit Teilnehmern überschneiden, weil „missed“ ein eigenes Ereignis und kein Gegenteil von Teilnahme ist.',
              },
              {
                label: 'Show-up-Quote',
                // Zähler und Nenner wie auf /webinar-auswertungen: Teilnehmer ÷ rechtzeitig
                // Angemeldete (erste Anmeldung vor Sendestart), Webinare mit Tracking-Warnung
                // aus beiden heraus.
                value: pct(data.webinarEngagement.attendees_ok, data.webinarEngagement.timely_ok),
                hint: `Teilnehmer ÷ rechtzeitig Angemeldete (Anmeldung vor Sendestart), wie auf /webinar-auswertungen. ${fmt(data.webinarEngagement.attendees_ok)} von ${fmt(data.webinarEngagement.timely_ok)}${data.webinarEngagement.webinars_excluded_for_tracking > 0 ? ` — ${fmt(data.webinarEngagement.webinars_excluded_for_tracking)} Webinar(e) mit Tracking-Warnung nicht mitgerechnet` : ''}. Bei den derzeitigen Fallzahlen eine Richtungsgröße, keine belastbare Quote.`,
              },
            ].map((card) => (
              <Tooltip key={card.label} title={card.hint} arrow>
                <Box
                  sx={{
                    border: '1px solid rgba(128,128,128,0.4)',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    minWidth: 150,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h6">{card.value}</Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
          {/* Rest gegen `persons` bilden, nicht gegen `registrations`: Teilnehmer und
          No-Shows zählen je Person (Webinar + E-Mail), eine doppelt abgesendete
          Formularzeile wäre sonst ein „ausstehendes Webinar", das es nicht gibt. */}
          {data.webinarEngagement.persons -
            data.webinarEngagement.attendees -
            data.webinarEngagement.no_shows >
            0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {fmt(
                data.webinarEngagement.persons -
                  data.webinarEngagement.attendees -
                  data.webinarEngagement.no_shows,
              )}{' '}
              Anmeldung(en) ohne Teilnahme-Ergebnis — das Webinar hat noch nicht stattgefunden oder
              das Ereignis ist noch nicht eingetroffen.
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Eine Aufschlüsselung nach auslösendem Mailing ist nicht möglich: die Anmeldungen dieser
            Kohorte tragen durchgängig keine Mailing-Kennung.
          </Typography>

          {/* ---- (f) Vergleich ---- */}
          {data.comparison && (
            <>
              <Typography variant="h5" sx={{ mb: 1, mt: 3 }}>
                Vergleich (Quasi-Kontrolle)
              </Typography>
              <Alert severity={data.comparison.significant ? 'success' : 'info'} sx={{ mb: 2 }}>
                Verglichen wird die Welcome-Kohorte mit den Anmeldern des Vormonats (
                {de(data.comparison.controlWindow.start)} bis{' '}
                {deInclusiveEnd(data.comparison.controlWindow.end)}), die nie in die Serie
                aufgenommen wurden. Beide Seiten sind auf{' '}
                <strong>{data.comparison.tenureDays} Tage ab der eigenen Anmeldung</strong> gekappt
                und enthalten nur Personen, deren Anmeldung so lange zurückliegt — sonst gewänne die
                Vormonatsgruppe allein dadurch, dass sie länger Zeit hatte.{' '}
                <strong>Der Block hängt nicht am Zeitraumfilter oben</strong>, weil die
                Kontrollgruppe ein fester historischer Monat ist.
                {!data.comparison.decidable && (
                  <>
                    {' '}
                    <strong>
                      Noch nicht aussagekräftig: mindestens {data.comparison.minBuyersPerArm} Käufer
                      je Seite nötig.
                    </strong>
                  </>
                )}
              </Alert>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Gruppe</TableCell>
                      <TableCell align="right">Personen</TableCell>
                      <TableCell align="right">Käufer</TableCell>
                      <TableCell align="right">
                        Kaufquote
                        <InfoTip text="Anteil der Personen mit mindestens einer Bestellung im Beobachtungsfenster. Bewusst je Person statt je Order — ein einzelner Mehrfachkäufer würde eine Gruppe dieser Größe sonst sichtbar verschieben." />
                      </TableCell>
                      {/* Absolutwerte MÜSSEN als solche beschriftet sein: die Gruppen
                      sind 2,4× verschieden groß, also führen beide Spalten die
                      Vormonatsgruppe vorn, während sie bei der Kaufquote hinten
                      liegt — genau der Fehlschluss, den die Quote verhindern soll. */}
                      <TableCell align="right">
                        Orders (absolut)
                        <InfoTip text="Summe, nicht je Person. Die Vormonatsgruppe ist gut doppelt so groß wie die Kohorte — für den Vergleich zählt allein die Kaufquote." />
                      </TableCell>
                      <TableCell align="right">
                        CPA-Erlös (absolut)
                        <InfoTip text="Summe über die ganze Gruppe. Skaliert mit der Gruppengröße und ist deshalb kein Leistungsvergleich." />
                      </TableCell>
                      <TableCell align="right">
                        Orders je 1.000
                        <InfoTip text="Auf die Gruppengröße normiert — die Spalte, die neben den Absolutwerten tatsächlich vergleichbar ist." />
                      </TableCell>
                      <TableCell align="right">Webinar-Anmelder</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { label: 'Vormonat ohne Serie', row: data.comparison.control },
                      { label: 'Welcome-Kohorte', row: data.comparison.treatment },
                    ].map((entry) => (
                      <TableRow key={entry.label} hover>
                        <TableCell>{entry.label}</TableCell>
                        <TableCell align="right">{fmt(entry.row.members)}</TableCell>
                        <TableCell align="right">{fmt(entry.row.buyers)}</TableCell>
                        <TableCell align="right">
                          <strong>{rate(entry.row.buyers, entry.row.members)}</strong>
                        </TableCell>
                        <TableCell align="right">{fmt(entry.row.orders)}</TableCell>
                        <TableCell align="right">{eur(entry.row.cpa_revenue)}</TableCell>
                        <TableCell align="right">
                          {entry.row.members > 0
                            ? dec((entry.row.orders / entry.row.members) * 1000, 2)
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {fmt(entry.row.webinar_registrants)} ·{' '}
                          {rate(entry.row.webinar_registrants, entry.row.members)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Unterschied der Kaufquote {dec(data.comparison.diff * 100, 2)} Prozentpunkte
                {data.comparison.pValue !== null && ` · p = ${dec(data.comparison.pValue, 3)}`}
                {data.comparison.ci95Low !== null &&
                  data.comparison.ci95High !== null &&
                  ` · 95 %-Intervall ${dec(data.comparison.ci95Low * 100, 2)} bis ${dec(data.comparison.ci95High * 100, 2)} pp`}
                .{' '}
                {data.comparison.significant
                  ? 'Der Unterschied ist statistisch abgesichert — bei einem nicht randomisierten Vergleich bleibt er trotzdem ein Indiz, kein Beweis.'
                  : 'Kein abgesicherter Unterschied: das Intervall schließt die Null ein, der beobachtete Abstand ist mit reinem Zufall vereinbar.'}
              </Typography>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>Kein A/B-Test.</strong> Wer in welcher Gruppe landet, entscheidet der
                Anmeldemonat, nicht der Zufall. Konkret heißt das:
                {/* MUIs CSS-Reset nimmt Listen den Marker — ohne das explizite
                listStyleType liest sich die Aufzählung als Fließtextblock. */}
                <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0, listStyleType: 'disc' }}>
                  <li>
                    <strong>
                      Gemessen wird nicht „Serie gegen nichts“, sondern „Serie statt
                      Direkt-Mailing“.
                    </strong>{' '}
                    Bis zum 30.07.2026 hat die Serie reguläre PLX-Direkt-Mailings an frische
                    Anmelder blockiert — alle hier gezählten Kohortenmitglieder haben ihr komplettes
                    Fenster unter dieser Sperre verbracht, die Vormonatsgruppe dagegen nicht. Der
                    Kanal-Block oben zeigt dieselbe Sache von der anderen Seite: Direkt-Mailing
                    steht bei der Kohorte auf 0. Die Serie ersetzt dort also Umsatz, statt ihn nur
                    hinzuzufügen.
                  </li>
                  <li>
                    Ein Monat Versatz — Saisonalität, Kampagnendruck und Herkunftsmix der Anmelder
                    sind nicht kontrolliert.
                  </li>
                  <li>
                    Beide Seiten schließen Personen aus, die vor ihrer Anmeldung schon einmal
                    gekauft hatten oder bereits an DNV übergeben waren. Ohne diesen Schnitt wäre der
                    Vergleich wertlos — die Bestandskäufer fehlen der Kohorte per Definition.
                  </li>
                  <li>
                    Einseitig bereinigt ist dagegen der Listen-Import: aus der Kohorte sind die
                    Profile ohne echte Anmeldung entfernt, aus der Vormonatsgruppe nicht (dort fehlt
                    das Kennzeichen, die Abgrenzung liegt in einer anderen Datenbank). Das betrifft
                    schätzungsweise 3 % ihres Nenners und lässt die Serie eher schlechter aussehen.
                  </li>
                  <li>
                    Nicht ausgleichbar ist der Abmeldestand: für die Vormonatsgruppe ist nur der
                    heutige bekannt, nicht der von damals. Wer sich seither abgemeldet hat, zählt
                    dort weiter mit — das drückt eher die Vergleichsgruppe.
                  </li>
                  <li>
                    Anmelder seit dem Start, die aus anderen Gründen keine Serie bekommen haben,
                    fehlen in beiden Gruppen.
                  </li>
                </Box>
              </Alert>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default EvaluationTab;
